/* eslint-disable react/forbid-prop-types */
/**
 * Loads a PDF document. Passes it to all children.
 */
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes, { Requireable } from 'prop-types';
import makeEventProps from 'make-event-props';
import makeCancellable from 'make-cancellable-promise';
import clsx from 'clsx';
import invariant from 'tiny-invariant';
import warning from 'tiny-warning';
import * as pdfjs from 'pdfjs-dist';

import DocumentContext from './DocumentContext';
import { useScrolling, useUnmount, useUpdateEffect } from 'react-use';
import Message from './Message';

import LinkService from './LinkService';
import PasswordResponses from './PasswordResponses';

import {
    cancelRunningTask,
    dataURItoByteString,
    displayCORSWarning,
    isArrayBuffer,
    isBlob,
    isBrowser,
    isDataURI,
    loadFromFile,
} from './shared/utils';

import useResolver from './shared/hooks/useResolver';
import { eventProps, isClassName, isFile, isRef } from './shared/propTypes';

import { PDFWorker, type PDFDocumentProxy } from 'pdfjs-dist';
import type { EventProps } from 'make-event-props';
import type {
    ClassName,
    DocumentCallback,
    ExternalLinkRel,
    ExternalLinkTarget,
    File,
    ImageResourcesPath,
    NodeOrRenderer,
    OnDocumentLoadError,
    OnDocumentLoadProgress,
    OnDocumentLoadSuccess,
    OnError,
    OnItemClickArgs,
    OnPasswordCallback,
    Options,
    PasswordResponse,
    RenderMode,
    ScrollPageIntoViewArgs,
    Source,
} from 'react-pdf/dist/cjs/shared/types';
import mergeRefs from 'merge-refs';
import BaseCanvasFactory from './CustomCanvasFactory';
import { DOMWasmFactory } from './display/dom';

const { PDFDataRangeTransport } = pdfjs;

type OnItemClick = (args: OnItemClickArgs) => void;

type OnPassword = (
    callback: OnPasswordCallback,
    reason: PasswordResponse
) => void;

type OnSourceError = OnError;

type OnSourceSuccess = () => void;

export interface PageScrollTo {
    offset: number;
    top: number;
    left: number;
    behavior: 'smooth' | 'instant' | 'auto';
    pageNumber: number;
}

export interface PDFDocumentProxyWithStep
    extends Omit<
        PDFDocumentProxy,
        | 'annotationStorage'
        | 'onCopy'
        | 'fingerprints'
        | 'getPage'
        | 'getOptionalContentConfig'
        | 'loadingParams'
        | 'loadingTask'
        | 'getMetadata'
    > {
    step?: number;
    worker?: Worker;
    pdfWorker?: PDFWorker;
    annotationStorage: any;
    fingerprints: any;
    getPage: (pageNumber: number) => any;
    onCopy?: any;
    getOptionalContentConfig: () => any;
    getMetadata: () => any;
    loadingParams: any;
    loadingTask: any;
}

export type PDFDocumentProxyWithLoadStep = Omit<
    PDFDocumentProxyWithStep,
    'canvasFactory'
>;
export type DocumentProps = {
    docNumber?: number;
    step?: number;
    children?: React.ReactNode;
    className?: ClassName;
    error?: NodeOrRenderer;
    externalLinkRel?: ExternalLinkRel;
    externalLinkTarget?: ExternalLinkTarget;
    file?: File;
    imageResourcesPath?: ImageResourcesPath;
    inputRef?: React.Ref<HTMLDivElement>;
    loading?: NodeOrRenderer;
    noData?: NodeOrRenderer;
    onItemClick?: OnItemClick;
    onLoadError?: OnDocumentLoadError;
    onLoadCancelation?: OnDocumentLoadError;
    onLoadNetworkError?: OnDocumentLoadError;
    onLoadProgress?: OnDocumentLoadProgress;
    onLoadSuccess?: OnDocumentLoadSuccess;
    onPassword?: OnPassword;
    onSourceError?: OnSourceError;

    onSourceSuccess?: OnSourceSuccess;
    onScrolling?: (val: boolean) => void;
    onRefreshDocument?: () => void;
    options?: Options;
    renderMode?: RenderMode;
    rotate?: number | null;
} & EventProps<DocumentCallback | false | undefined>;

const defaultOnPassword: OnPassword = (callback, reason) => {
    switch (reason) {
        case PasswordResponses.NEED_PASSWORD: {
            // eslint-disable-next-line no-alert
            const password = prompt(
                'Enter the password to open this PDF file.'
            );
            callback(password);
            break;
        }
        case PasswordResponses.INCORRECT_PASSWORD: {
            // eslint-disable-next-line no-alert
            const password = prompt('Invalid password. Please try again.');
            callback(password);
            break;
        }
        default:
    }
};

const Document = forwardRef(
    (
        {
            step,
            docNumber,
            children,
            className,
            error = 'Failed to load PDF file.',
            externalLinkRel,
            externalLinkTarget,
            file,
            inputRef,
            imageResourcesPath,
            loading = 'Loading PDF…',
            noData = 'No PDF file specified.',
            onItemClick,
            onLoadError: onLoadErrorProps,
            onLoadCancelation: onLoadCancelationProps,
            onLoadNetworkError: onLoadNetworkErrorProps,
            onLoadProgress,
            onLoadSuccess: onLoadSuccessProps,
            onPassword = defaultOnPassword,
            onSourceError: onSourceErrorProps,

            onSourceSuccess: onSourceSuccessProps,
            onScrolling,
            options,
            renderMode,
            rotate,
            onRefreshDocument,
            ...otherProps
        }: DocumentProps,
        ref
    ) => {
        const workerPath = useMemo(
            () =>
                window.location.href
                    .split('/')
                    .slice(0, 3)
                    .includes('localhost:3000')
                    ? new URL(
                          'http://localhost:3000/js/pdf.worker.min.mjs',
                          import.meta.url
                      ).toString()
                    : new URL(
                          `${window.location.href
                              .split('/')
                              .slice(0, 3)
                              .join('/')}/js/pdf.worker.min.mjs`,
                          import.meta.url
                      ).toString(),
            []
        );
        const [worker, setWorker] = useState<Worker | null>(null);
        const [workerPDF, setWorkerPDF] = useState<PDFWorker | null>(null);

        const [sourceState, sourceDispatch] = useResolver<Source | null>();
        const [isNetworkError, setNetworkError] = useState<boolean>(false);
        const [isCancelation, setCancelation] = useState<boolean>(false);
        const { value: source, error: sourceError } = sourceState;
        const [loadingTask, setLoadingTask] = useState<any>(null);
        const [loadingFile, setLoadingFile] = useState<File | undefined>(null);
        const [pdfState, pdfDispatch] = useResolver<PDFDocumentProxyWithStep>();
        const { value: pdf, error: pdfError } = pdfState;
        const innerInputRef = useRef<null | HTMLDivElement>(null);
        const isScrolling = useScrolling(innerInputRef);

        const linkService = useRef(new LinkService());

        const pages = useRef<HTMLDivElement[]>([]);

        useEffect(() => {
            if (typeof onScrolling === 'function') {
                onScrolling(isScrolling);
            }
        }, [isScrolling]);

        const viewer = useRef({
            // Handling jumping to internal links target
            scrollPageIntoView: (args: ScrollPageIntoViewArgs) => {
                const { dest, pageNumber, pageIndex = pageNumber - 1 } = args;

                // First, check if custom handling of onItemClick was provided
                if (onItemClick) {
                    onItemClick({ dest, pageIndex, pageNumber });
                    return;
                }

                // If not, try to look for target page within the <Document>.
                const page = pages.current[pageIndex];

                if (page) {
                    // Scroll to the page automatically
                    page.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                    return;
                }

                warning(
                    false,
                    `An internal link leading to page ${pageNumber} was clicked, but neither <Document> was provided with onItemClick nor it was able to find the page within itself. Either provide onItemClick to <Document> and handle navigating by yourself or ensure that all pages are rendered within <Document>.`
                );
            },
            scrollTo: (options: PageScrollTo) => {
                const { left, top, behavior, pageNumber, offset } = options;
                const pageIndex = pageNumber - 1;
                // If not, try to look for target page within the <Document>.
                const page = pages.current[pageIndex];
                // innerInputRef.current?.scrollTo();

                if (page) {
                    // console.log('test scroll', left, top, behavior);
                    // Scroll to the page automatically
                    innerInputRef.current?.scrollBy({
                        left,
                        top: offset + top,
                        behavior,
                    });
                    return;
                }

                warning(
                    false,
                    `An internal link leading to page ${pageNumber} was clicked, but neither <Document> was provided with onItemClick nor it was able to find the page within itself. Either provide onItemClick to <Document> and handle navigating by yourself or ensure that all pages are rendered within <Document>.`
                );
            },
        });
        const [isResetedHook, setResetedHook] = useState<boolean>(false);

        const sourceReference = useRef<string | null>('');
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const resetSource = useCallback(
            (fileSource?: any) => {
                sourceDispatch({ type: 'RESET' });
                if (
                    (sourceReference.current !== '' &&
                        fileSource !== sourceReference.current) ||
                    fileSource === ''
                ) {
                    const workerThread = new Worker(workerPath, {
                        type: 'module',
                    }) as any;

                    setWorker((prev) => {
                        if (prev !== null) {
                            prev.terminate();
                        }
                        return workerThread;
                    });

                    setWorkerPDF((prev) => {
                        if (prev !== null && prev.destroyed === false) {
                            prev.destroy();
                        }

                        return new PDFWorker({ port: workerThread });
                    });
                }
                sourceReference.current = fileSource;
            },
            [sourceDispatch, workerPath]
        );

        useUnmount(() => {
            if (worker !== null) {
                worker.terminate();
            }
            if (workerPDF !== null && workerPDF.destroyed === false) {
                workerPDF.destroy();
            }
        });

        useImperativeHandle(
            ref,
            () => ({
                linkService,
                pages,
                viewer,
                resetSource: () => {
                    resetSource();
                    setResetedHook(true);
                },
            }),
            [resetSource]
        );

        /**
         * Called when a document source is resolved correctly
         */
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function onSourceSuccess() {
            if (onSourceSuccessProps) {
                onSourceSuccessProps();
            }
        }

        /**
         * Called when a document source failed to be resolved correctly
         */
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function onSourceError() {
            if (!sourceError) {
                // Impossible, but TypeScript doesn't know that
                return;
            }

            warning(false, sourceError.toString());

            if (onSourceErrorProps) {
                onSourceErrorProps(sourceError);
            }
        }

        useEffect(() => {
            resetSource(file);
        }, [file, sourceDispatch]);

        const findDocumentSource =
            useCallback(async (): Promise<Source | null> => {
                if (!file) {
                    return null;
                }

                // File is a string
                if (typeof file === 'string') {
                    if (isDataURI(file)) {
                        const fileByteString = dataURItoByteString(file);
                        return { data: fileByteString };
                    }

                    displayCORSWarning();
                    return { url: file };
                }

                // File is PDFDataRangeTransport
                if (file instanceof PDFDataRangeTransport) {
                    return { range: file };
                }

                // File is an ArrayBuffer
                if (isArrayBuffer(file)) {
                    return { data: file };
                }

                /**
                 * The cases below are browser-only.
                 * If you're running on a non-browser environment, these cases will be of no use.
                 */
                if (isBrowser) {
                    // File is a Blob
                    if (isBlob(file)) {
                        const data = await loadFromFile(file);

                        return { data };
                    }
                }

                // At this point, file must be an object
                invariant(
                    typeof file === 'object',
                    'Invalid parameter in file, need either Uint8Array, string or a parameter object'
                );

                invariant(
                    'data' in file || 'range' in file || 'url' in file,
                    'Invalid parameter object: need either .data, .range or .url'
                );

                // File .url is a string
                if ('url' in file && typeof file.url === 'string') {
                    if (isDataURI(file.url)) {
                        const { url, ...otherParams } = file;
                        const fileByteString = dataURItoByteString(url);
                        return { data: fileByteString, ...otherParams };
                    }

                    displayCORSWarning();
                }

                return file;
            }, [file]);

        // eslint-disable-next-line consistent-return
        useEffect(() => {
            const cancellable = makeCancellable(findDocumentSource());

            cancellable.promise
                .then((nextSource) => {
                    sourceDispatch({ type: 'RESOLVE', value: nextSource });
                })
                .catch((error) => {
                    sourceDispatch({ type: 'REJECT', error });
                });

            return () => {
                cancelRunningTask(cancellable);
            };
        }, [findDocumentSource, sourceDispatch, isResetedHook]);

        // eslint-disable-next-line consistent-return
        useEffect(() => {
            if (isResetedHook) {
                const cancellable = makeCancellable(findDocumentSource());

                cancellable.promise
                    .then((nextSource) => {
                        sourceDispatch({ type: 'RESOLVE', value: nextSource });
                    })
                    .catch((error) => {
                        sourceDispatch({ type: 'REJECT', error });
                    });
                setResetedHook(false);
                return () => {
                    cancelRunningTask(cancellable);
                };
            }
        }, [isResetedHook]);

        useEffect(
            () => {
                if (typeof source === 'undefined') {
                    return;
                }

                if (source === false) {
                    onSourceError();
                    return;
                }

                onSourceSuccess();
            },
            // Ommitted callbacks so they are not called every time they change
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [source]
        );

        /**
         * Called when a document is read successfully
         */
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function onLoadSuccess() {
            if (!pdf) {
                // Impossible, but TypeScript doesn't know that
                return;
            }
            linkService.current.setDocument(pdf);
            if (onLoadSuccessProps) {
                onLoadSuccessProps(pdf);
            }

            pages.current = new Array(pdf.numPages);
        }

        /**
         * Called when a document failed to read successfully
         */
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function onLoadError() {
            if (!pdfError) {
                // Impossible, but TypeScript doesn't know that
                return;
            }

            warning(false, pdfError.toString());

            if (onLoadErrorProps) {
                onLoadErrorProps(pdfError);
            }
        }

        /**
         * Called when a document failed to read successfully
         */
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function onLoadCancelation() {
            if (!pdfError) {
                // Impossible, but TypeScript doesn't know that
                return;
            }

            warning(false, pdfError.toString());

            if (onLoadCancelationProps) {
                onLoadCancelationProps(pdfError);
            }
        }
        /**
         * Called when a document failed by network error
         */
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unused-vars
        function onLoadNetworkError() {
            if (!pdfError) {
                // Impossible, but TypeScript doesn't know that
                return;
            }

            warning(false, pdfError.toString());

            if (onLoadNetworkErrorProps) {
                onLoadNetworkErrorProps(pdfError);
            }
        }

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function resetDocument() {
            pdfDispatch({ type: 'RESET' });
        }

        useEffect(resetDocument, [pdfDispatch, source]);

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function loadDocument(
            currentStep: any,
            worker: Worker | null,
            pdfWorker: PDFWorker | null
        ) {
            if (!source || worker === null || pdfWorker === null) {
                return;
            }

            const documentInitParams = options
                ? {
                      ...source,
                      ...options,
                  }
                : source;

            const destroyable = pdfjs.getDocument({
                ...documentInitParams,
                CanvasFactory: BaseCanvasFactory,
                disableStream: true,
                disableAutoFetch: true,
                worker: pdfWorker,
                WasmFactory: DOMWasmFactory,
                wasmUrl: `${window.location.protocol}//${window.location.host}/js/openjpeg/`,
                useWasm: true,
            });
            if (onLoadProgress) {
                destroyable.onProgress = onLoadProgress;
            }
            if (onPassword) {
                destroyable.onPassword = onPassword;
            }
            const loadingTask = destroyable;
            setLoadingTask(loadingTask);
            setLoadingFile(file);
            loadingTask.promise
                .then((nextPdf) => {
                    const nextStepPdf: PDFDocumentProxyWithStep = nextPdf;
                    nextStepPdf.step = currentStep;
                    nextStepPdf.worker = worker;
                    nextStepPdf.pdfWorker = pdfWorker;
                    pdfDispatch({
                        type: 'RESOLVE',
                        value: nextStepPdf,
                    });
                    // pdfDispatch({ type: 'RESOLVE', value: nextPdf });
                    setCancelation(false);
                    setLoadingTask(null);
                    setLoadingFile(null);
                })
                .catch((error) => {
                    if (loadingTask.destroyed) {
                        return;
                    }

                    if (
                        error.name === 'UnknownErrorException' &&
                        error.details === 'Error: Canceled new step'
                    ) {
                        setCancelation(true);
                    } else {
                        setCancelation(false);
                    }

                    if (
                        error.name === 'UnknownErrorException' &&
                        error.details === 'TypeError: Failed to fetch'
                    ) {
                        setNetworkError(true);
                    } else {
                        setNetworkError(false);
                    }

                    setLoadingTask(null);
                    setLoadingFile(null);
                    pdfDispatch({ type: 'REJECT', error });
                });

            // eslint-disable-next-line consistent-return
            return () => {
                loadingTask.destroy();
            };
        }
        useUpdateEffect(() => {
            if (loadingTask && typeof step !== 'undefined') {
                // eslint-disable-next-line no-underscore-dangle
                if (loadingTask._transport) {
                    // eslint-disable-next-line no-underscore-dangle

                    // eslint-disable-next-line no-underscore-dangle
                    const { _fullReader } = loadingTask._transport;
                    if (_fullReader) {
                        // const { _reader } = _fullReader;
                        // if (_reader) {
                        //     _reader.cancel();
                        // }
                        // eslint-disable-next-line no-underscore-dangle
                        const stream = _fullReader._stream;
                        if (
                            stream &&
                            stream.source &&
                            stream.source.url === loadingFile
                        ) {
                            // eslint-disable-next-line no-underscore-dangle
                            const abortProtocol: AbortController =
                                // eslint-disable-next-line no-underscore-dangle
                                _fullReader._abortController;
                            if (abortProtocol.signal.aborted === false) {
                                abortProtocol.abort(
                                    new Error('Canceled new step')
                                );
                            }
                        }
                    }
                }
            }
        }, [step]);
        useEffect(
            () => {
                loadDocument(step, worker, workerPDF);
            },

            // Ommitted callbacks so they are not called every time they change
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [options, pdfDispatch, source, docNumber]
        );

        useEffect(
            () => {
                if (typeof pdf === 'undefined') {
                    return;
                }
                if (isCancelation) {
                    if (pdf !== false && pdf.worker) {
                        pdf.worker.terminate();
                    }
                    if (
                        pdf !== false &&
                        pdf.pdfWorker &&
                        pdf.pdfWorker.destroyed === false
                    ) {
                        pdf.pdfWorker.destroy();
                    }
                    setCancelation(false);
                    onLoadCancelation();
                    return;
                }

                if (isNetworkError) {
                    if (pdf !== false && pdf.worker) {
                        pdf.worker.terminate();
                    }
                    if (
                        pdf !== false &&
                        pdf.pdfWorker &&
                        pdf.pdfWorker.destroyed === false
                    ) {
                        pdf.pdfWorker.destroy();
                    }
                    setNetworkError(false);
                    onLoadNetworkError();
                    return;
                }

                if (pdf === false) {
                    onLoadError();
                    return;
                }
                if (pdf.step !== step) {
                    if (pdf.worker) {
                        pdf.worker.terminate();
                    }
                    if (pdf.pdfWorker && pdf.pdfWorker.destroyed === false) {
                        pdf.pdfWorker.destroy();
                    }
                }

                onLoadSuccess();
            },
            // Ommitted callbacks so they are not called every time they change
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [pdf, docNumber]
        );

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function setupLinkService() {
            linkService.current.setViewer(viewer.current);
            linkService.current.setExternalLinkRel(externalLinkRel);
            linkService.current.setExternalLinkTarget(externalLinkTarget);
        }

        useEffect(setupLinkService, [externalLinkRel, externalLinkTarget]);

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function registerPage(pageIndex: number, ref: HTMLDivElement) {
            pages.current[pageIndex] = ref;
        }

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function unregisterPage(pageIndex: number) {
            delete pages.current[pageIndex];
        }

        const handleDocumentNeedRefresh = React.useCallback(() => {
            if (typeof onRefreshDocument === 'function') {
                onRefreshDocument();
            }
        }, [onRefreshDocument]);
        const childContext = useMemo(
            () => ({
                imageResourcesPath,
                linkService: linkService.current,
                onItemClick,
                pdf,
                registerPage,
                renderMode,
                rotate,
                unregisterPage,
                isScrolling,
                step: step ?? 0,
                onRefresh: handleDocumentNeedRefresh,
            }),
            [
                handleDocumentNeedRefresh,
                imageResourcesPath,
                isScrolling,
                onItemClick,
                pdf,
                renderMode,
                rotate,
                step,
            ]
        );

        const eventProps = useMemo(
            () => makeEventProps({ ...otherProps }, () => pdf),
            [otherProps, pdf]
        );

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function renderChildren() {
            return (
                <DocumentContext.Provider value={childContext}>
                    {children}
                </DocumentContext.Provider>
            );
        }

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function renderContent() {
            if (!file) {
                return (
                    <Message type="no-data">
                        {typeof noData === 'function' ? noData() : noData}
                    </Message>
                );
            }

            if (pdf === undefined || pdf === null) {
                return (
                    <Message type="loading">
                        {typeof loading === 'function' ? loading() : loading}
                    </Message>
                );
            }

            if (pdf === false) {
                return (
                    <Message type="error">
                        {typeof error === 'function' ? error() : error}
                    </Message>
                );
            }

            return renderChildren();
        }

        return (
            <div
                className={clsx('react-pdf__Document', className)}
                ref={mergeRefs(inputRef, innerInputRef)}
                {...eventProps}
            >
                {renderContent()}
            </div>
        );
    }
);

const node: Requireable<React.ReactNode> =
    PropTypes.node as unknown as Requireable<React.ReactNode>;
const isFunctionOrNode = PropTypes.oneOfType([PropTypes.func, node]);

Document.propTypes = {
    ...eventProps,
    docNumber: PropTypes.number,
    step: PropTypes.number,
    children: node,
    className: isClassName,
    error: isFunctionOrNode,
    externalLinkRel: PropTypes.string,
    externalLinkTarget: PropTypes.oneOf(['_self', '_blank', '_parent', '_top']),
    file: isFile,
    imageResourcesPath: PropTypes.string,
    inputRef: isRef,
    loading: isFunctionOrNode,
    noData: isFunctionOrNode,
    onItemClick: PropTypes.func,
    onLoadError: PropTypes.func,
    onLoadCancelation: PropTypes.func,
    onLoadNetworkError: PropTypes.func,
    onLoadProgress: PropTypes.func,
    onLoadSuccess: PropTypes.func,
    onPassword: PropTypes.func,
    onSourceError: PropTypes.func,

    onSourceSuccess: PropTypes.func,
    options: PropTypes.shape({
        canvasFactory: PropTypes.any,
        canvasMaxAreaInBytes: PropTypes.number,
        cMapPacked: PropTypes.bool,
        CMapReaderFactory: PropTypes.any,
        cMapUrl: PropTypes.string,
        disableAutoFetch: PropTypes.bool,
        disableFontFace: PropTypes.bool,
        disableRange: PropTypes.bool,
        disableStream: PropTypes.bool,
        docBaseUrl: PropTypes.string,
        enableXfa: PropTypes.bool,
        filterFactory: PropTypes.any,
        fontExtraProperties: PropTypes.bool,
        httpHeaders: PropTypes.object,
        isEvalSupported: PropTypes.bool,
        isOffscreenCanvasSupported: PropTypes.bool,
        length: PropTypes.number,
        maxImageSize: PropTypes.number,
        ownerDocument: PropTypes.any,
        password: PropTypes.string,
        pdfBug: PropTypes.bool,
        rangeChunkSize: PropTypes.number,
        StandardFontDataFactory: PropTypes.any,
        standardFontDataUrl: PropTypes.string,
        stopAtErrors: PropTypes.bool,
        useSystemFonts: PropTypes.bool,
        useWorkerFetch: PropTypes.bool,
        verbosity: PropTypes.number,
        withCredentials: PropTypes.bool,
        worker: PropTypes.any,
    }),
    rotate: PropTypes.number,
};

export default Document;
